import { useAuth } from "./useAuth.js";

export type UserProfile = "super_admin" | "org_admin" | "member" | "viewer";

interface Permissions {
  /** User profile label for UI display */
  profile: UserProfile;
  profileLabel: string;

  /** Core flags */
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  isMember: boolean;

  /** Module access */
  canViewAllOrgs: boolean;
  canSwitchOrg: boolean;
  canManageMembers: boolean;
  canManagePipelines: boolean;
  canManageAgents: boolean;
  canManageSites: boolean;
  canViewLogs: boolean;
  canViewInfra: boolean;
  canViewVault: boolean;
  canViewSecurity: boolean;
  canViewGovernance: boolean;
  canViewPlugins: boolean;
  canViewChannels: boolean;
  canViewMemory: boolean;
  canViewSystem: boolean;
  canViewLLM: boolean;
  canManageIntegrations: boolean;
  canManageConstitution: boolean;
  canManageSkills: boolean;

  /** Data scope */
  dataScope: "global" | "organization" | "personal";
}

export function usePermissions(): Permissions {
  const { user } = useAuth();

  const role = user?.role || "member";
  const isSuperAdmin = (user as any)?.isSuperAdmin === true;
  const isOwner = role === "owner";
  const isAdmin = role === "admin";
  const isOrgAdmin = isOwner || isAdmin;
  const isMember = role === "member";

  // Determine profile
  let profile: UserProfile = "member";
  let profileLabel = "Membro";
  if (isSuperAdmin) {
    profile = "super_admin";
    profileLabel = "Admin YOS";
  } else if (isOrgAdmin) {
    profile = "org_admin";
    profileLabel = isOwner ? "Owner" : "Admin";
  } else if (role === "viewer") {
    profile = "viewer";
    profileLabel = "Visualizador";
  }

  // Data scope
  let dataScope: "global" | "organization" | "personal" = "personal";
  if (isSuperAdmin) dataScope = "global";
  else if (isOrgAdmin) dataScope = "organization";

  return {
    profile,
    profileLabel,
    isSuperAdmin,
    isOrgAdmin,
    isMember,

    // Module access
    canViewAllOrgs: isSuperAdmin,
    canSwitchOrg: isSuperAdmin,
    canManageMembers: isOrgAdmin || isSuperAdmin,
    canManagePipelines: isOrgAdmin || isSuperAdmin,
    canManageAgents: isOrgAdmin || isSuperAdmin,
    canManageSites: isOrgAdmin || isSuperAdmin,
    canViewLogs: true, // all roles can view logs (filtered by scope)
    canViewInfra: isOrgAdmin || isSuperAdmin,
    canViewVault: isOrgAdmin || isSuperAdmin,
    canViewSecurity: isOrgAdmin || isSuperAdmin,
    canViewGovernance: isOrgAdmin || isSuperAdmin,
    canViewPlugins: isOrgAdmin || isSuperAdmin,
    canViewChannels: isOrgAdmin || isSuperAdmin,
    canViewMemory: isOrgAdmin || isSuperAdmin,
    canViewSystem: isOrgAdmin || isSuperAdmin,
    canViewLLM: isOrgAdmin || isSuperAdmin,
    canManageIntegrations: isOrgAdmin || isSuperAdmin,
    canManageConstitution: isOrgAdmin || isSuperAdmin,
    canManageSkills: isOrgAdmin || isSuperAdmin,

    dataScope,
  };
}
